import React, { useCallback, useMemo } from "react";
import {
  Carousel,
  ContentCard,
  ContentCardSkeleton,
  EmptyState,
} from "~/components";
import {
  PASSION_DEFINITIONS,
  POPULAR_PASSION_IDS,
  usePassionsData,
} from "~/hooks/usePassionsData";
import { usePassionsHubHotels } from "~/hooks/usePassionsHubHotels";
import { usePassionsStore } from "~/store";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";
import { useShallow } from "zustand/react/shallow";
import placeholderImage from "~/assets/common/img_placeholder.jpg";

const CDN_IMAGE_PREFIX = "https://cache.marriott.com/";
// Show passion badge overlay for any passion with propensity rating > 0.5
export const PASSION_PROPENSITY_THRESHOLD = 0.5;
// If only one passion exists, show 3 hotels for that passion
const MAX_SINGLE_PASSION_TILES = 3;
// Explore Hotels link opens Marriott default search in new tab
const EXPLORE_HOTELS_URL = "https://www.marriott.com/default.mi";

const normalizeLabel = (id, fallbackLabel) => {
  if (fallbackLabel) {
    return fallbackLabel;
  }

  const definition = id ? PASSION_DEFINITIONS[id] : null;
  if (definition?.label) {
    return definition.label;
  }

  if (!id) {
    return "";
  }

  return id
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const buildImageUrl = (rawUrl) => {
  if (!rawUrl) {
    return undefined;
  }

  if (/^https?:\/\//i.test(rawUrl)) {
    return rawUrl;
  }

  const trimmed = String(rawUrl).replace(/^\/+/, "");
  return `${CDN_IMAGE_PREFIX}${trimmed}`;
};

const hasNavigationLink = (property) =>
  Boolean(property?.navigationLink) &&
  typeof property.navigationLink === "string";

const buildTile = (property, { passionMeta, actionPassion } = {}) => {
  const passionId = passionMeta?.id || actionPassion?.id || null;
  const label = normalizeLabel(
    passionId,
    actionPassion?.label || actionPassion?.name || passionMeta?.label,
  );
  const icon =
    passionMeta?.icon || PASSION_DEFINITIONS[passionId]?.icon || null;
  const propensity = passionMeta?.propensity ?? null;
  const shouldShowBadge = passionMeta?.isFallback
    ? true
    : propensity !== null && propensity >= PASSION_PROPENSITY_THRESHOLD;

  // Extract review data from UXL response
  const reviews = property.reviews || {};

  return {
    id: property.id,
    name: property.name || `Property ${property.id}`,
    description: property.description,
    image: buildImageUrl(property.imageUrl) || placeholderImage,
    navigationLink: property.navigationLink,
    passionLabel: label,
    passionId,
    propensity,
    // Review data from UXL query
    reviewCount: reviews.count || null,
    reviewRating: reviews.rating || null,
    reviewStars: reviews.stars || null,
    reviewDescription: reviews.description || null,
    activities:
      shouldShowBadge && icon
        ? [
            {
              label,
              icon,
            },
          ]
        : [],
  };
};

const toUpperId = (value) => String(value || "").toUpperCase();

export const selectHotelTiles = (hotelsData, passionMetaList) => {
  if (
    !hotelsData ||
    !Array.isArray(passionMetaList) ||
    passionMetaList.length === 0
  ) {
    return [];
  }

  const passionMetaById = new Map(
    passionMetaList.map((entry) => [entry.id, entry]),
  );

  const propertyDetailsById = new Map();
  (hotelsData.properties || []).forEach((property) => {
    if (property?.id) {
      propertyDetailsById.set(toUpperId(property.id), property);
    }
  });

  const actionPassionById = new Map();
  (hotelsData.passions || []).forEach((entry) => {
    const passionId = String(entry.id || entry.name || "").toLowerCase();
    const displayLabel = entry.label || entry.name || null;
    const propertyIds = Array.isArray(entry.properties) ? entry.properties : [];

    actionPassionById.set(passionId, {
      id: passionId,
      label: displayLabel,
      name: entry.name || null,
      propertyIds,
    });
  });

  const resolvePropertyDetails = (propertyIds) =>
    propertyIds
      .map((propertyId) => propertyDetailsById.get(toUpperId(propertyId)))
      .filter((property) => property && hasNavigationLink(property));

  if (passionMetaList.length === 1) {
    const soleMeta = passionMetaList[0];
    const actionPassion = actionPassionById.get(soleMeta.id);
    const propertyIds = actionPassion?.propertyIds || [];

    const candidates = resolvePropertyDetails(propertyIds).slice(
      0,
      MAX_SINGLE_PASSION_TILES,
    );

    return candidates.map((property) =>
      buildTile(property, {
        passionMeta: passionMetaById.get(soleMeta.id),
        actionPassion,
      }),
    );
  }

  const tiles = [];

  passionMetaList.forEach((meta) => {
    const actionPassion = actionPassionById.get(meta.id);
    const propertyIds = actionPassion?.propertyIds || [];
    const property = resolvePropertyDetails(propertyIds)[0];

    if (!property) {
      return;
    }

    tiles.push(
      buildTile(property, {
        passionMeta: passionMetaById.get(meta.id),
        actionPassion,
      }),
    );
  });

  return tiles;
};

export const HotelsCarousel = () => {
  const {
    getSelectedPassionsMeta,
    loading: passionsLoading,
    error: passionsError,
  } = usePassionsData();

  // Get selected passions from store for reactive updates (guest selections in CollectionsModule)
  // Use useShallow to properly detect array content changes
  const storeSelectedPassions = usePassionsStore(
    useShallow((state) => state.selectedPassions),
  );

  const selectedPassionMeta = getSelectedPassionsMeta();

  // Build passion meta list based on:
  // 1. Store passions (guest selections) - highest priority
  // 2. Profile passions (from CDP) - if logged in
  // 3. Popular passions (fallback) - if no passions exist or user not in CDP
  const passionMetaList = useMemo(() => {
    // If store has passions (guest selections from CollectionsModule), use those
    if (storeSelectedPassions && storeSelectedPassions.length > 0) {
      return storeSelectedPassions.map((id) => ({
        id,
        label: normalizeLabel(id),
        icon: PASSION_DEFINITIONS[id]?.icon || null,
        propensity: 0.75,
        isFallback: false,
      }));
    }

    // Otherwise use profile passions from CDP
    if (selectedPassionMeta.length > 0) {
      return selectedPassionMeta.map((entry) => ({
        ...entry,
        label: normalizeLabel(entry.id, entry.label),
      }));
    }

    // Fallback: If NO passions exist or user not in CDP, show "most popular" passions
    return POPULAR_PASSION_IDS.map((id) => ({
      id,
      label: normalizeLabel(id),
      icon: PASSION_DEFINITIONS[id]?.icon || null,
      propensity: 0.75,
      isFallback: true,
    }));
    // Use JSON.stringify to ensure we detect content changes in the arrays
  }, [selectedPassionMeta, storeSelectedPassions]);

  const passionIds = passionMetaList.map((entry) => entry.id);
  // If only one passion exists, show 3 hotels; otherwise show 1 hotel per passion
  const effectiveLimit = passionIds.length === 1 ? MAX_SINGLE_PASSION_TILES : 1;

  const {
    data: hotelsData,
    loading: hotelsLoading,
    error: hotelsError,
  } = usePassionsHubHotels(passionIds, { limit: effectiveLimit });

  const hotelTiles = useMemo(
    () => selectHotelTiles(hotelsData, passionMetaList),
    [hotelsData, passionMetaList],
  );

  const loading = passionsLoading || hotelsLoading;

  const handleExploreHotelsClick = useCallback(() => {
    if (typeof window !== "undefined" && window?.open) {
      window.open(EXPLORE_HOTELS_URL, "_blank", "noopener,noreferrer");
    }
  }, []);

  if (loading) {
    return (
      <div style={{}}>
        <Carousel
          title={"Explore Hotels"}
          onTitleClick={handleExploreHotelsClick}
          showNavigation={true}
          gap={32}
        >
          {Array.from({ length: 6 }, (_, index) => (
            <ContentCardSkeleton key={index} />
          ))}
        </Carousel>
      </div>
    );
  }

  if (passionsError) {
    console.warn(
      "Error loading passions, falling back to defaults:",
      passionsError,
    );
  }

  if (hotelsError) {
    console.warn("Error loading hotels by passion:", hotelsError);
  }

  return (
    <div style={{}}>
      {hotelTiles.length > 0 ? (
        <Carousel
          title={"Explore Hotels"}
          onTitleClick={handleExploreHotelsClick}
          showNavigation={true}
          id="hotels-by-passion-carousel"
          gap={32}
        >
          {hotelTiles.map((hotel) => {
            const isSingleSlide = hotelTiles.length === 1;
            const cardStyle = isSingleSlide
              ? { width: "830px", maxWidth: "calc(100vw - 48px)" }
              : undefined;

            const imageStyle = isSingleSlide
              ? { height: "440px", width: "100%", objectFit: "cover" }
              : undefined;

            let imageSrc = hotel.image;
            if (
              isSingleSlide &&
              imageSrc &&
              !imageSrc.includes("placeholder")
            ) {
              const separator = imageSrc.includes("?") ? "&" : "?";
              imageSrc = `${imageSrc}${separator}wid=1660&fit=constrain`;
            }

            return (
              <ContentCard
                key={hotel.id}
                style={cardStyle}
                imageStyle={imageStyle}
                title={hotel.name}
                description={
                  hotel.description ||
                  (hotel.passionLabel
                    ? `Curated for ${hotel.passionLabel}`
                    : "")
                }
                image={imageSrc}
                activities={hotel.activities}
                rating={hotel.reviewRating}
                reviewCount={hotel.reviewCount}
                hotelLink={
                  hotel.navigationLink
                    ? {
                        url: hotel.navigationLink,
                        target: "_blank",
                        rel: "noopener noreferrer",
                      }
                    : undefined
                }
                {...getAnalyticsClickTrackingAttributes(
                  "Explore Hotels",
                  "click",
                  `Opened ${hotel.name}`,
                )}
              />
            );
          })}
        </Carousel>
      ) : (
        <EmptyState
          icon="hotel"
          title="No hotel recommendations yet"
          subtitle="Select your passions above to discover curated hotels"
        />
      )}
    </div>
  );
};
